import { useTranslation } from 'react-i18next';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';
import { useEffect } from 'react';

import HomePage from '../../pages/homePage';
import AboutPage from '../../pages/aboutPage';
import TeamPage from '../../pages/teampage';
import BlogPage from '../../pages/blogPage';
import BlogPostPage from '../../pages/blogPostPage';
import FaqPage from '../../pages/faqPage';
import ContactUsPage from '../../pages/contactUsPage';
import AccessibilityPage from '../../pages/accessibilityPage';
import PrivacyPolicy from '../../pages/privacyPolicy';
import LegalDisclaimer from '../../pages/legalDisclaimer';
import PageNotFound from '../../pages/404PageNotFound';

import ImmigrationPage from '../../pages/practiceAreas/immigrationPage';
import PersonalInjuryPage from '../../pages/practiceAreas/personalInjuryPage';
import LemonLawPage from '../../pages/practiceAreas/lemonLawPage';
// import WorkersCompensationPage from '../../pages/practiceAreas/workersCompensationPage';
import EmploymentLawPage from '../../pages/practiceAreas/employmentLaw';

// import GlendaleOffice from '../../pages/locations/glendaleOffice';
// import SacramentoOffice from '../../pages/locations/sacramentoOffice';

const SUPPORTED_LANGS = new Set(['en', 'ru', 'hy', 'es']);

// ✅ FORCE /en → /en/
const LangRedirect = () => {
  const { lang } = useParams();
  return <Navigate to={`/${lang}/`} replace />;
};

// ✅ Language handler
/**
 * An unsupported "lang" segment used to unconditionally redirect to /en/,
 * which meant this site had no real 404 page: any dead/mistyped link (and
 * react-snap's own automatic /404.html probe - it always requests exactly
 * that path after its second crawled page, see puppeteer_utils.js) matches
 * this same param and was silently sent to the homepage instead of showing
 * "page not found". That's a soft-404 for search engines and confusing for
 * anyone who lands on a broken link, and it's why the generated
 * build/404.html - the file most static hosts serve for genuinely unmatched
 * paths - was just a copy of the homepage (react-snap logs a warning about
 * this: "404 page title does not contain '404' string"). Rendering
 * PageNotFound directly for an unsupported lang, instead of navigating away,
 * fixes both at once and needs no extra route.
 */
const LanguageRouterWrapper = ({ children }) => {
  const { i18n } = useTranslation();
  const { lang } = useParams();

  const isSupported = Boolean(lang) && SUPPORTED_LANGS.has(lang);

  useEffect(() => {
    if (isSupported && i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
  }, [lang, i18n, isSupported]);

  if (!isSupported) {
    return <PageNotFound />;
  }

  return children;
};

const AppRoutes = ({ myRef }) => (
  <Routes>
    {/*
      react-snap always crawls exactly this literal path to generate a
      static build/404.html for the web server to hand out on real 404s
      (see puppeteer_utils.js - it's automatic, not something in our own
      config). Without this route, "/404.html" fell through to /:lang
      (lang="404.html") -> LangRedirect -> a client-side <Navigate> that
      adds a trailing slash before ever rendering PageNotFound. That's fine
      the first time react-snap crawls it (it just follows the redirect),
      but the FILE gets saved under the path originally requested
      ("/404.html", no slash) while its content reflects the app AFTER that
      redirect. So anyone whose browser loads build/404.html directly at its
      real, literal path - which is the whole point of the file - starts
      fresh at "/404.html" (no slash) again, tries to render that same
      <Navigate> (which renders nothing), and hydration finds a full 404
      page already sitting in the DOM instead: "Minified React error #418",
      every time. Matching the route directly, with nothing in between,
      means there's never a redirect to get out of sync with.
    */}
    <Route path="/404.html" element={<PageNotFound />} />

    {/*
      Same redirect-then-mismatch bug as /404.html above, hitting the single
      most important URL on the whole site: the bare domain root. react-snap
      crawls "/", follows this <Navigate>, settles at "/en/", and saves the
      RESULTING (post-redirect) HTML under the path it originally requested -
      "/" - i.e. build/index.html. Any real visitor typing avagyanlaw.com, or
      any backlink/bookmark pointing at the bare domain, loads that file fresh
      at "/", where this route used to render nothing but a <Navigate>
      (redirecting the *client*, not the already-rendered static HTML) against
      a DOM that's already a full homepage. Hydration finds them out of sync
      and throws "Minified React error #418" - this was very likely a real
      contributor to the production crash originally reported on the live
      homepage, separate from the footer.jsx text-node bug. Rendering
      HomePage directly here removes the redirect hop entirely, so there's
      nothing for a fresh load to get out of sync with. This is safe:
      HomePage doesn't read a :lang param (i18n.js's getLangFromUrl already
      defaults unmatched paths like "/" to "en"), and every internal link it
      builds goes through withLang(), which likewise defaults "/" to the
      "en" prefix - so "/" and "/en/" already render byte-for-byte identical
      markup and link targets. SEOUpdater already special-cases "/" to
      canonicalize to "/en/", so no SEO signal changes either.
    */}
    <Route path="/" element={<HomePage myRef={myRef} />} />

    {/* /en → /en/ */}
    <Route path="/:lang" element={<LangRedirect />} />

    {/* HOME */}
    <Route
      path="/:lang/"
      element={
        <LanguageRouterWrapper>
          <HomePage myRef={myRef} />
        </LanguageRouterWrapper>
      }
    />

    {/* PAGES */}
    <Route
      path="/:lang/about"
      element={
        <LanguageRouterWrapper>
          <AboutPage />
        </LanguageRouterWrapper>
      }
    />
    <Route
      path="/:lang/immigration"
      element={
        <LanguageRouterWrapper>
          <ImmigrationPage />
        </LanguageRouterWrapper>
      }
    />
    <Route
      path="/:lang/personalinjury"
      element={
        <LanguageRouterWrapper>
          <PersonalInjuryPage />
        </LanguageRouterWrapper>
      }
    />
    <Route
      path="/:lang/lemonlaw"
      element={
        <LanguageRouterWrapper>
          <LemonLawPage />
        </LanguageRouterWrapper>
      }
    />
    {/*<Route*/}
    {/*  path="/:lang/workerscomp"*/}
    {/*  element={*/}
    {/*    <LanguageRouterWrapper>*/}
    {/*      <WorkersCompensationPage />*/}
    {/*    </LanguageRouterWrapper>*/}
    {/*  }*/}
    {/*/>*/}
    <Route
      path="/:lang/employmentlaw"
      element={
        <LanguageRouterWrapper>
          <EmploymentLawPage />
        </LanguageRouterWrapper>
      }
    />

    {/* OFFICE LOCATION LANDING PAGES - coming next, see /src/pages/locations */}
    {/*<Route*/}
    {/*  path="/:lang/glendale-office"*/}
    {/*  element={*/}
    {/*    <LanguageRouterWrapper>*/}
    {/*      <GlendaleOffice />*/}
    {/*    </LanguageRouterWrapper>*/}
    {/*  }*/}
    {/*/>*/}
    {/*<Route*/}
    {/*  path="/:lang/sacramento-office"*/}
    {/*  element={*/}
    {/*    <LanguageRouterWrapper>*/}
    {/*      <SacramentoOffice />*/}
    {/*    </LanguageRouterWrapper>*/}
    {/*  }*/}
    {/*/>*/}

    <Route
      path="/:lang/team"
      element={
        <LanguageRouterWrapper>
          <TeamPage />
        </LanguageRouterWrapper>
      }
    />
    {/*<Route*/}
    {/*  path="/:lang/useful-links"*/}
    {/*  element={*/}
    {/*    <LanguageRouterWrapper>*/}
    {/*      <UsefulLinks />*/}
    {/*    </LanguageRouterWrapper>*/}
    {/*  }*/}
    {/*/>*/}
    <Route
      path="/:lang/blog"
      element={
        <LanguageRouterWrapper>
          <BlogPage />
        </LanguageRouterWrapper>
      }
    />
    <Route
      path="/:lang/blog/:blogTitle"
      element={
        <LanguageRouterWrapper>
          <BlogPostPage />
        </LanguageRouterWrapper>
      }
    />
    <Route
      path="/:lang/faq"
      element={
        <LanguageRouterWrapper>
          <FaqPage />
        </LanguageRouterWrapper>
      }
    />
    <Route
      path="/:lang/contact-us"
      element={
        <LanguageRouterWrapper>
          <ContactUsPage />
        </LanguageRouterWrapper>
      }
    />
    <Route
      path="/:lang/accessibility"
      element={
        <LanguageRouterWrapper>
          <AccessibilityPage />
        </LanguageRouterWrapper>
      }
    />
    <Route
      path="/:lang/privacy-policy"
      element={
        <LanguageRouterWrapper>
          <PrivacyPolicy />
        </LanguageRouterWrapper>
      }
    />
    <Route
      path="/:lang/legal-disclaimer"
      element={
        <LanguageRouterWrapper>
          <LegalDisclaimer />
        </LanguageRouterWrapper>
      }
    />

    {/* 404 */}
    <Route
      path="/:lang/*"
      element={
        <LanguageRouterWrapper>
          <PageNotFound />
        </LanguageRouterWrapper>
      }
    />

    {/* fallback */}
    <Route path="*" element={<Navigate to="/en/" replace />} />
  </Routes>
);

export default AppRoutes;
